<script setup lang="ts">
import CurrentDateTime from '@/components/CurrentDateTime/CurrentDateTime.vue';
import NotificationBar from '@/components/notifications/NotificationBar.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { getFilteredNavigation } from '@/navigation';
import { getRouteMetaMerged } from '@/router/utils';
import { useAuthenticationStore } from '@/stores/authenticationStore';
import { useSystemStore } from '@/stores/systemStore';
import { truthy } from '@/utils/helper';
import { mdiDotsHorizontal } from '@mdi/js';
import { useTranslation } from 'i18next-vue';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import Button from '@/components/buttons/Button.vue';
import { useNavigationOverflow } from '@/utils/useNavigationOverflow';

const route = useRoute();
const router = useRouter();

const systemStore = useSystemStore();
const authenticationStore = useAuthenticationStore();

const navigation = computed(() => {
  const targetRouteMeta = getRouteMetaMerged(route);
  return targetRouteMeta.subnavigation
    ? getFilteredNavigation(router, route, targetRouteMeta.subnavigation)
    : null;
});

const { t } = useTranslation();

const labelI18n = computed(() =>
  route.matched
    .map((match) => match.meta.labelI18n)
    .filter(truthy)
    .map((label) => t(label))
    .join(' | '),
);

const { navigationContainer, showOverflowMenu, visibleNavigation, overflowNavigation } =
  useNavigationOverflow(navigation);
</script>

<template>
  <div class="toolbar-wrapper">
    <v-toolbar class="subnavigationBar text-body-2" height="40" extensionHeight="40">
      <v-toolbar-title class="text-caption text-uppercase" aria-label="main-section">{{
        labelI18n
      }}</v-toolbar-title>

      <div v-if="navigation" ref="navigationContainer" class="navigation">
        <!-- Visible navigation items -->
        <Tooltip
          v-for="entry in visibleNavigation?.entries || []"
          :key="entry.titleI18n"
          :showPointer="true"
          :disabled="!entry.tooltipI18n"
          position="bottom"
          class="nav-item"
        >
          <template #target="{ props }">
            <router-link
              v-bind="props"
              class="link"
              activeClass="active"
              :aria-label="entry.titleI18n"
              :to="entry.route || ''"
              data-nav-item
            >
              {{ $t(entry.titleI18n) }}
            </router-link>
          </template>
          <template v-if="entry.tooltipI18n" #tooltip>
            {{ $t(entry.tooltipI18n) }}
          </template>
        </Tooltip>

        <!-- Overflow menu -->
        <v-menu v-if="overflowNavigation" v-model="showOverflowMenu" location="bottom">
          <template #activator="{ props: menuProps }">
            <Button
              v-bind="menuProps"
              variant="text"
              :icon="mdiDotsHorizontal"
              size="small"
              class="overflow-btn"
              aria-label="More navigation options"
            />
          </template>

          <v-list class="overflow-menu" density="compact">
            <v-list-item
              v-for="entry in overflowNavigation.entries"
              :key="entry.titleI18n"
              :to="entry.route || ''"
              :title="$t(entry.titleI18n)"
              class="overflow-item text-body-2"
            >
              <template v-if="entry.tooltipI18n" #subtitle>
                {{ $t(entry.tooltipI18n) }}
              </template>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <template #append>
        <div class="right text-body-2">
          <Tooltip position="bottom" :disabled="systemStore.isProductionEnvironment">
            <template #target="{ props }">
              <span class="ipin" v-bind="props">
                IPIN {{ authenticationStore?.session?.ipin }} | {{ t('Version') }}
                {{ systemStore.version }}
              </span>
            </template>
            <template #tooltip>
              {{ systemStore.productVersion }} | {{ systemStore.frontendVersion }} -
              <DateTimeFormatter :value="systemStore.frontendBuildDate" format="short" />
            </template>
          </Tooltip>
          <span class="datetime"><CurrentDateTime /></span>
        </div>
      </template>
      <template #extension>
        <NotificationBar />
      </template>
    </v-toolbar>
  </div>
</template>

<style lang="scss" scoped>
@use '@/styles/variables';
@use '@/styles/mixins';

.toolbar-wrapper {
  width: 100%;
  z-index: 11;
}

.subnavigationBar {
  background: rgb(var(--v-theme-secondary-darken-3));
  display: flex;
  align-items: center;
  color: #fff;
  height: variables.$subNavigationBarHeight;

  @include mixins.shadow-bottom(5);

  :deep(.v-toolbar__content) {
    padding: 0 variables.$horizontalAppPadding;
    display: flex;
    align-items: center;

    & > .v-toolbar-title {
      margin-inline-start: unset;
      flex-grow: 0;
      flex-shrink: 0;
      flex-basis: fit-content;
    }
  }
  :deep(.v-toolbar__extension) {
    height: auto !important;
  }

  .navigation {
    margin-left: 24px;
    padding-left: 18px;
    border-left: 1px solid rgba(255, 255, 255, 0.3);
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 48px; // Ensure space for at least the overflow button
    overflow: hidden;

    .nav-item {
      flex-shrink: 0;
    }

    .link {
      opacity: 0.6;
      color: #fff;
      padding: 0 6px;
      text-decoration: none;
      white-space: nowrap;
      text-align: center;

      &:hover {
        opacity: 1;
      }

      &.active {
        opacity: 1;
      }
    }

    .overflow-btn {
      opacity: 0.6;
      color: #fff;
      margin-left: 0;
      margin-right: 32px;
      flex-shrink: 0;

      &:hover {
        opacity: 1;
      }
    }
  }

  .right {
    margin-left: auto;
    flex-shrink: 0;
  }

  .ipin {
    opacity: 0.5;
  }

  .datetime {
    padding-left: 24px;
  }
}
</style>

<style lang="scss">
.overflow-menu {
  background: rgb(var(--v-theme-secondary-darken-3)) !important;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  color: rgb(var(--v-theme-surface), 0.8);

  .overflow-item {
    &:hover {
      background: rgba(var(--v-theme-secondary-lighten-2), 0.1);
    }

    &.v-list-item--active {
      background: rgba(var(--v-theme-secondary-lighten-2), 0.2);
    }
  }
}
</style>
